<template>
  <div class="certificate">
    <Header></Header>
    <div class="container">
      <div class="mt-5 mb-5">
        <div v-if="loading" class="loading-container">
            <ShieldLoader></ShieldLoader>
        </div>
        <div v-else class="view-container p-5">
            <div v-if="certification && certification.full_conformance" class="d-flex flex-column">
                <h2 class="text-center pb-3">
                    Certification of WCAG {{ certification.wcag_version }} Level A and AA Conformance
                </h2>

                <img :alt="`Certified WCAG ${certification.wcag_version} AA Fully Conformant`" :src="`https://badges.onlineada.com/full/svg/certifiedbadge_fully${certification.wcag_version}.png`" class="d-block mx-auto pb-2">

                <p class="mb-5">This digital certificate confirms that {{certification.website_platform}} was audited and certified as WCAG {{ certification.wcag_version }} Level A and AA conformant on {{certification.date_certified}}.
                <div class="mb-2" v-if="certification.override_text" v-html="certification.text_override"></div>
                <div v-else>
                    <h3 class="mb-2">What this Means</h3>

                    <p class="mb-3">
                        <span class="text-capitalize">{{certification.client_name}}</span> considers digital accessibility a vital element in today's digital landscape. Ensuring that {{certification.website_platform}} is perceivable, operable, and understandable by people with one or more physical or cognitive disabilities, whether temporary or permanent, is important to us. As such, we have set forth a clear plan to achieve <span v-if="certification && certification.ocm">and maintain</span> the digital accessibility of {{certification.website_platform}}. Our goal is to achieve level AA conformance as set forth by the internationally accepted accessibility standard, WCAG {{ certification.wcag_version }}. To help us achieve <span v-if="certification && certification.ocm">and maintain</span> conformance <span v-if="certification && certification.ocm">over time</span>, we have enlisted the services of Online ADA, a national leader in digital accessibility consulting services.
                    </p>

                    <p class="mb-5">
                        It is our pledge to our customers that we will do everything in our power to ensure that {{certification.website_platform}} meets the Level AA WCAG {{ certification.wcag_version }} standard. This standard continually evolves; interpretation of the guidelines is fluid and can shift as accessibility professionals more fully examine their implications and application.
                    </p>

                    <h4 class="mb-2">Conformance Status: Full Conformance</h4>

                    <p :class="{'mb-3':certification && certification.ocm, 'mb-5': certification && !certification.ocm}">
                        Online ADA has analyzed {{certification.website_platform}} using industry-standard automated and manual auditing procedures to identify WCAG violations. Our team has fixed the identified violations and Online ADA has confirmed that all issues have been remediated so that the {{certification.website_platform}} meets or exceeds the WCAG {{ certification.wcag_version }} level AA standard.
                    </p>

                    <p v-if="certification && certification.ocm" class="mb-5">
                        Online ADA will audit and re-certify our platform at regular intervals throughout the year to ensure {{certification.website_platform}} remains conformant to WCAG {{ certification.wcag_version }} Level AA. Additionally, we will receive monthly reports of the conformance health of {{certification.website_platform}} from Online ADA's automated scans so that we can promptly remediate new issues as they arise. We believe that the combination of our internal efforts with the support offered by Online ADA makes {{certification.website_platform}} accessible to users with motor, visual, auditory, or cognitive impairments.
                    </p>

                    <h4 class="mb-2">Feedback & Formal Complaints</h4>

                    <p class="mb-5">
                        If, for any reason, you find that a part of {{certification.website_platform}} does not meet the WCAG {{ certification.wcag_version }}, Level AA standard, or find that a part could have its accessibility improved, please reach out to us and we will review your feedback promptly and carefully.
                        <ul>
                            <li>Phone: <span v-html="phoneLink"></span></li>
                            <li>Email: <span v-html="emailLink"></span></li>
                        </ul>
                    </p>
        
                    <div v-show="showCompatibilitySection">
                        <h4 class="mb-2">Compatibility</h4>

                        <div class="mb-3" v-if="certification && certification.compatibility_specifics.length">
                            <p class="mb-0"><span class="text-capitalize">{{certification.website_platform}}</span> is designed to be compatible with the following assistive technologies:</p>
                            <ul>
                                <li v-for="(ob, i) in certification.compatibility_specifics" :key="'compatibility-specifics'+i">{{ob}}</li>
                            </ul>
                        </div>
                        <div class="mb-4" v-if="certification && certification.browser_versions.length">
                            <p class="mb-0"><span class="text-capitalize">{{certification.website_platform}}</span> is not compatibile with:</p>
                            <ul>
                                <li>browsers older than 
                                    <template v-for="(ob, i) in certification.browser_versions">
                                        <span :key="'bvfirst-'+i">{{ob.name}} {{ob.version}}</span>
                                        <span :key="'bvsecond-'+i" v-if="(i + 1) != certification.browser_versions.length && (i + 1) != certification.browser_versions.length - 1">, </span>
                                        <span :key="'bvthird-'+i" v-if="(i + 1) == (certification.browser_versions.length - 1)"> and </span>
                                    </template>
                                    or mobile operating systems older than {{certification.num_years}} years.
                                </li>
                                
                            </ul>
                        </div>
                    </div>
                    <h4 class="mb-2">Web Technology Requirements</h4>

                    <p class="mb-0">
                        Accessibility of {{certification.website_platform}} relies on the following technologies to work with the particular combination of web browser and any assistive technologies or plugins installed on your computer:
                    </p>
                    <ul class="mb-2">
                        <li>HTML</li>
                        <li>WAI-ARIA</li>
                        <li>CSS</li>
                        <li>JavaScript</li>
                    </ul>
                    <p class="mb-5">These technologies are relied upon for conformance with the accessibility standards referenced.</p>

                    <div v-if="certification && certification.limitations.length">
                        <h4 class="mb-2">Limitations and alternatives</h4>
                    
                        <p class="mb-2">
                            Despite our best efforts to ensure accessibility of {{certification.website_platform}}, there may be some limitations. Below is a description of known limitations, and potential solutions. Please contact us if you observe an issue not listed below.

                            Known limitations for {{certification.website_platform}}:
                        </p>
                        <ul>
                            <li v-for="(ob, i) in certification.limitations" :key="'limitation-'+i">{{ob}}</li>
                        </ul>
                    </div>
                </div>
            </div>
            <div v-else class="d-flex flex-column">
                <h2 class="text-center pb-3">
                    Certification of WCAG {{ certification.wcag_version }} Level A and AA Partial Conformance
                </h2>

              <img :alt="`Certified WCAG ${certification.wcag_version} AA Partial Conformant`" :src="`https://badges.onlineada.com/partial/svg/certifiedbadge_partial${certification.wcag_version}.png`" class="d-block mx-auto pb-2">

                
                <p class="mb-5">This digital certificate confirms that {{certification.website_platform}} was audited and certified as WCAG {{ certification.wcag_version }} Level A and AA partially conformant on {{certification.date_certified}}.
                <div class="mb-2" v-if="certification.override_text" v-html="certification.text_override"></div>
                <div v-else>
                    <h3 class="mb-2">What this Means</h3>

                    <p class="mb-2">
                        <span class="text-capitalize">{{certification.client_name}}</span> considers digital accessibility a vital element in today's digital landscape. Ensuring that {{certification.website_platform}} is perceivable, operable, and understandable by people with one or more physical or cognitive disabilities, whether temporary or permanent, is important to us. As such, we have set forth a clear plan to achieve <span v-if="certification && certification.ocm">and maintain</span> the digital accessibility of {{certification.website_platform}}. Our goal is to achieve level AA conformance as set forth by the internationally accepted accessibility standard, WCAG {{ certification.wcag_version }}. To help us achieve <span v-if="certification && certification.ocm">and maintain</span> conformance <span v-if="certification.ocm">over time</span>, we have enlisted the services of Online ADA, a national leader in digital accessibility consulting services.
                    </p>

                    <p class="mb-5">
                        It is our pledge to our customers that we will do everything in our power to ensure that {{certification.website_platform}} meets the Level AA WCAG {{ certification.wcag_version }} standard. This standard continually evolves; interpretation of the guidelines is fluid and can shift as accessibility professionals more fully examine their implications and application.
                    </p>

                    <h4 class="mb-2">Conformance Status: Partial Conformance</h4>

                    <p class="mb-2">
                        Online ADA has analyzed {{certification.website_platform}} using industry-standard automated and manual auditing procedures to identify WCAG violations. Our team has fixed the identified violations, with exceptions as noted below, and Online ADA has confirmed that if the excepted content were removed, {{certification.website_platform}} would meet or exceed the WCAG {{ certification.wcag_version }} level AA standard.
                    </p>

                    <p style="font-size:18px" class="mb-2">Exceptions</p>
                    <ol>
                        <li v-for="(exception, i) in certification.exceptions" :key="'exception-'+i">{{exception}}</li>
                    </ol>

                    <p class="mt-2" v-if="certification && certification.ocm">
                        Online ADA will audit and re-certify our platform at regular intervals throughout the year to ensure {{certification.website_platform}} remains conformant to WCAG {{ certification.wcag_version }} Level AA. Additionally, we will receive monthly reports of the conformance health of {{certification.website_platform}} from Online ADA's automated scans so that we can promptly remediate new issues as they arise. We believe that the combination of our internal efforts with the support offered by Online ADA makes {{certification.website_platform}} accessible to users with motor, visual, auditory, or cognitive impairments.
                    </p>

                    <h4 class="mb-2 mt-4">Feedback & Formal Complaints</h4>

                    <p class="mb-2">
                        If, for any reason, you find that a part of {{certification.website_platform}} does not meet the WCAG {{ certification.wcag_version }}, Level AA standard, or find that a part could have its accessibility improved, please reach out to us and we will review your feedback promptly and carefully.
                        <ul>
                            <li>Phone: <span v-html="phoneLink"></span></li>
                            <li>Email: <span v-html="emailLink"></span></li>
                        </ul>
                    </p>
        
                    <div class="mb-5" v-show="showCompatibilitySection">
                        <h4 class="mb-2 mt-2">Compatibility</h4>

                        <div class="mb-2" v-if="certification && certification.compatibility_specifics.length">
                            <p class="mb-0"><span class="text-capitalize">{{certification.website_platform}}</span> is designed to be compatible with the following assistive technologies:</p>
                            <ul>
                                <li v-for="(ob, i) in certification.compatibility_specifics" :key="'compatibility-specifics'+i">{{ob}}</li>
                            </ul>
                        </div>
                        <div v-if="certification && certification.browser_versions.length">
                            <p class="mb-0"><span class="text-capitalize">{{certification.website_platform}}</span> is not compatibile with:</p>
                            <ul>
                                <li>browsers older than 
                                    <template v-for="(ob, i) in certification.browser_versions">
                                        <span :key="'bvfirst-'+i">{{ob.name}} {{ob.version}}</span>
                                        <span :key="'bvsecond-'+i" v-if="(i + 1) != certification.browser_versions.length && (i + 1) != certification.browser_versions.length - 1">, </span>
                                        <span :key="'bvthird-'+i" v-if="(i + 1) == (certification.browser_versions.length - 1)"> and </span>
                                    </template>
                                    or mobile operating systems older than {{certification.num_years}} years.
                                </li>
                                
                            </ul>
                        </div>
                    </div>
                    <h4 class="mb-2">Web Technology Requirements</h4>

                    <p class="mb-0">
                        Accessibility of {{certification.website_platform}} relies on the following technologies to work with the particular combination of web browser and any assistive technologies or plugins installed on your computer:
                    </p>
                    <ul class="mb-2">
                        <li>HTML</li>
                        <li>WAI-ARIA</li>
                        <li>CSS</li>
                        <li>JavaScript</li>
                    </ul>
                    <p class="mb-5">These technologies are relied upon for conformance with the accessibility standards referenced.</p>

                    <div v-if="certification && certification.limitations.length">
                        <h4 class="mb-2">Limitations and alternatives</h4>
                    
                        <p class="mb-2">
                            Despite our best efforts to ensure accessibility of {{certification.website_platform}}, there may be some limitations. Below is a description of known limitations, and potential solutions. Please contact us if you observe an issue not listed below.

                            Known limitations for {{certification.website_platform}}:
                        </p>
                        <ul>
                            <li v-for="(ob, i) in certification.limitations" :key="'limitation-'+i">{{ob}}</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div><!-- end container -->
        
      </div>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import Header from '@/components/Header.vue'
import moment from 'moment-timezone'
import ShieldLoader from '../components/RouteLoader'

export default {
    name: 'Certificate',
    data: () => ({
        loading: true,
        certificate_url: false,
        certification: false
    }),
    computed: {
        showCompatibilitySection(){
            if( this.certification ){
                if(this.certification.browser_versions.length ||
                    this.certification.compatibility_specifics.length){
                    return true;
                }
            }
            
            return false;
        },
        phoneLink(){
            return "<a style='color:black' href='tel:"+this.certification.phone+"' >"+this.certification.phone+"</a>";
        },
        emailLink(){
            return "<a style='color:black' href='mailto:"+this.certification.email+"' >"+this.certification.email+"</a>";
        }
    },
    watch:{
        "$store.state.certification": function(val) {
            if(val){
                this.certification = val;
                this.certification.date_certified = moment.tz(this.certification.date_certified, "UTC").format("LL");
                if(this.certification.limitations === null || !this.certification.limitations.length) this.certification.limitations = [];
                if(this.certification.exceptions === null || !this.certification.exceptions.length) this.certification.exceptions = [];
                if(this.certification.browser_versions === null || !this.certification.browser_versions.length) this.certification.browser_versions = [];
                if(this.certification.compatibility_specifics === null || !this.certification.compatibility_specifics.length) this.certification.compatibility_specifics = [];
                this.loading = false;
            }
        }
    },
    created() {
        this.certificate_url = this.$route.params.certificate_url;
        
        this.$store.dispatch("Init", {
            slug: this.certificate_url,
            router: this.$router
        });
    },
    components: {
        Header,
        ShieldLoader
    }
    }
</script>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Open+Sans&display=swap');
svg{    
    width: 275px;
    height: 275px;
}
.certificate .view-container{
    color: #000;
    font-family: 'Open Sans';
}
.cls-1, .cls-2 {
    fill: #46394b;
}
.cls-2 {
    stroke: #fff;
    stroke-miterlimit: 10;
    stroke-width: 1.04px;
}
.cls-3 {
    fill: #fff;
}
</style>
